import React, { useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  Form,
  Button,
  Col,
  Row,
  Input,
  Space,
  notification,
} from 'antd';
import { tagsSelector } from '../store';
import { isValidUrl } from '../utils/isValidUrl';
import { useAppDispatch, useAppSelector } from 'src/hooks/store';

import { Tag, fetchAllTags } from 'src/slices/tag';
import {
  fetchMetaDataOfArticle,
  MetaData,
  resetLoadingMetaData,
} from 'src/slices/metadata';
import { fetchCurrentTeamSettings } from 'src/slices/auth';

import { ArticleInformation } from 'src/components/ArticleInformation';
import { AddArticleForm } from 'src/components/AddArticleForm';

const useStyles = createUseStyles({
  addArticleForm: {},
  urlButton: {
    display: 'inline-flex',
    flexDirection: 'row',
    flewWrap: 'wrap',
  },
  addArticleButton: {
    position: 'fixed',
    right: '15px',
    bottom: '15px',
    width: '10vh',
    height: '10vh',
    zIndex: '100',
    '& .ant-btn': {
      width: '100%',
      height: '100%',
    },
    '& .anticon': {
      fontSize: '5vh',
    },
  },
});

export const openNotificationWithlogo = (
  type: string,
  message: string,
  description: string,
) => {
  const content = {
    message: message,
    description: description,
  };
  switch (type) {
    case 'success':
      notification.success(content);
      break;
    case 'error':
      notification.error(content);
      break;
    default:
      notification.info(content);
      break;
  }
};

interface Props {
  defaultTag?: string;
  teamId: string | undefined;
}

const AddArticle: React.VFC<Props> = ({ defaultTag, teamId }) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);

  const {
    metaDatas,
    loading,
  }: {
    metaDatas: MetaData;
    loading: boolean;
  } = useAppSelector((state) => state.metaDatas);

  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [openFormArticle, setOpenFormArticle] = useState<boolean>(false);

  const [formArticle] = Form.useForm();
  const [formUrl] = Form.useForm();

  const { team, tags, isLoading } = useAppSelector((state) => ({
    team: state.auth.team,
    tags: tagsSelector.selectAll(state),
    isLoading: state.tag.isLoading,
  }));

  useEffect(() => {
    if (teamId) {
      if (!team) {
        dispatch(fetchCurrentTeamSettings(teamId));
      }
      dispatch(fetchAllTags(teamId));
    }
  }, [teamId]);

  const addUrl = ({ url }: { url: string }) => {
    setOpenFormArticle(true);

    const getMetaData = async () => {
      const result = await dispatch(fetchMetaDataOfArticle(url));
      if (fetchMetaDataOfArticle.rejected.match(result)) {
        const message = result.error.message
          ? t(`errors.${result.error.message}`)
          : '';
        openNotificationWithlogo(
          'error',
          t('errors.error'),
          `${result.meta.arg} : ${message}`,
        );
        resetDrawer();
      }
    };

    getMetaData();
  };

  const resetDrawer = () => {
    formArticle.resetFields();
    formUrl.resetFields();
    setOpenFormArticle(false);
    dispatch(resetLoadingMetaData());
  };

  const closeDrawer = (e: any) => {
    e.preventDefault();
    setOpenDrawer(false);
    resetDrawer();
  };

  return (
    <div className={classes.addArticleForm}>
      <div className={classes.addArticleButton}>
        <Button
          type="primary"
          shape="circle"
          size="large"
          onClick={() => setOpenDrawer(true)}
          icon={<PlusOutlined />}
        />
      </div>
      <Drawer
        title={t('addArticleTranslation.addArticle')}
        width={'50vw'}
        onClose={(e) => closeDrawer(e)}
        visible={openDrawer}
        bodyStyle={{ paddingBottom: 80 }}
      >
        {!openFormArticle && (
          <Form layout="horizontal" onFinish={addUrl} form={formUrl}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="url"
                  label="Url"
                  rules={[
                    {
                      required: true,
                      message: t('common.urlInput'),
                      validator: (_, value) =>
                        isValidUrl(value)
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error('Should be a correct url !'),
                            ),
                    },
                  ]}
                >
                  <Input
                    style={{ width: '100%' }}
                    placeholder={t('common.urlInput')}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Space className={classes.urlButton}>
                  <Button onClick={() => formUrl.resetFields()}>
                    {t('common.cancelBtn')}
                  </Button>
                  <Button type="primary" htmlType="submit">
                    {t('common.addBtn')}
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form>
        )}
        {openFormArticle && (
          <ArticleInformation loading={loading} metaDatas={metaDatas} />
        )}
        {!loading && !isLoading && (
          <AddArticleForm
            defaultTag={defaultTag}
            metaDatas={metaDatas}
            tags={tags}
            selectedTags={selectedTags}
            setSelectedTags={setSelectedTags}
            resetDrawer={resetDrawer}
            closeDrawer={closeDrawer}
            teamId={teamId}
            team={team}
            formArticle={formArticle}
          />
        )}
      </Drawer>
    </div>
  );
};

export default AddArticle;
